import { useState } from 'react';
import { useFetcher } from 'react-router';
import { Button } from '@understory-io/leaf/button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  defaultVariantDurationSeconds,
  filterVariantsForDuration,
  hasSellableVariants as computeHasSellableVariants,
  remapTicketsForDuration,
} from '~/domain/experiences/duration-variants';
import type { DisplayTicketVariant } from '~/domain/experiences/variant-display';
import { formatDuration, FormattedPrice } from '~/ui/formatted';
import { Choice, ChoiceGroup } from '~/ui/choice-group';
import { TicketsSelector } from '~/ui/tickets-selector';
import { ampli } from '~/domain/plugins/analytics';
import { BookingPanelCard } from './booking-panel-card';

/** Form intent Book now posts under, matched by the experience route's action
 *  (which hands off to `handleOpenTimeSlotHandoff`). Declared here, next to the
 *  only sender, so the two can't drift. */
export const OPEN_TIME_SLOT_INTENT = 'book-open';

/**
 * Gives the duration chips a background here only — not a `ChoiceGroup`-level
 * style. Figma (31834-81672) paints them on `surface.base`/`surface.background`
 * because the widget's card sits on `surface.raised` (white); the open-flow
 * checkout steps' duration pills reuse the same `<Choice>` on a card that's
 * already `surface.base` (`CheckoutCard` with `flat`), where this background
 * would make the chip disappear against its own card. Scoping the override to
 * this call site (PR review feedback) keeps that surface intact.
 */
const durationChoiceStyles = css({ background: 'surface.base' });

/**
 * A ticket variant as the panel needs it: the display fields `TicketsSelector`
 * renders, plus the two fields the per-duration rules read (US-10975). Open
 * time slots sell each conceptual ticket once per duration — "Adult 1 hour" and
 * "Adult 2 hours" are separate variants sharing a `groupKey`. Alias of the
 * domain's shared display shape (also used by the open checkout flow's
 * tickets step) so the two call sites can't drift.
 */
export type OpenTimeSlotVariant = DisplayTicketVariant;

type BookingPanelOpenTimeSlotProps = {
  /** The experience whose panel this is — sent explicitly on Book now
   *  (`Experience Booking Panel Book Now Clicked` gets no `Checkout *`
   *  enrichment, unlike the checkout flow's own events). */
  experienceId: string;
  /** Whether the price carries the "From" prefix — the storefront's
   *  `showFromPrice` preference. */
  showFromPrice: boolean;
  defaultPrice?: {
    amountCents: number;
    vatExclusiveAmountCents: number;
    currency: string;
    exponent: number;
    vatExempt: boolean;
  } | null;
  /** The purchasable durations (seconds, ascending) — one pill each. Sourced
   *  from the experience's operating hours, the same place every step of the
   *  open checkout flow derives them from, so the pills can't offer a duration
   *  the flow would then reject. Empty when the operating hours couldn't be
   *  read; the widget then sells the shortest duration its variants carry. */
  durationsSeconds: number[];
  /** Every variant the experience sells, across all durations — the widget
   *  filters to the selected duration itself so switching a pill needs no
   *  round trip. */
  variants: OpenTimeSlotVariant[];
  /** Step for seat variants (the experience's multiple-of rule). */
  seatStep?: number;
  /** When false, checkout isn't reachable (the host cannot receive payments yet
   *  and we're not in preview) — the card shows PaymentsNotReadyState instead of
   *  the pickers and CTA. */
  canProceedToCheckout?: boolean;
};

/**
 * The tablet+ booking panel for an **open time slot** (operating hours)
 * experience: "From <price>", the duration pills, a ticket stepper per variant,
 * and Book now (Figma 31834-81672). The sibling panel for experiences that sell
 * scheduled events is `<BookingPanelScheduled>`; the route mounts one or the
 * other from the experience's availability mode.
 *
 * Open time slots have no fixed events to pick from, so there is no calendar here.
 * What the visitor answers on this page is *what* they want — duration and
 * tickets — and Book now hands that to the checkout's open flow, which asks
 * *where* (`/open/location`) and *when* (`/open/date`, `/open/time`) in turn.
 *
 * Duration and quantities are local state, not URL params: nothing on this page
 * outside the widget depends on them (open availability ignores language filters
 * and the location is chosen inside the flow), so a pill tap re-filters the
 * visible variants client-side instead of dimming the panel through a loader
 * round trip. They are committed exactly once, by Book now, which posts to the
 * experience route's `book-open` action.
 */
export function BookingPanelOpenTimeSlot({
  experienceId,
  showFromPrice,
  defaultPrice,
  durationsSeconds,
  variants,
  seatStep = 1,
  canProceedToCheckout = true,
}: BookingPanelOpenTimeSlotProps) {
  const intl = useIntl();
  const fetcher = useFetcher();

  // The shortest offered duration opens selected, mirroring the checkout steps'
  // `deriveOpenHoursSelection` default. With no operating hours to go by, fall
  // back to the shortest duration the variants themselves are priced at — that
  // keeps the picker from listing every duration's variants at once.
  const [durationSeconds, setDurationSeconds] = useState<number | null>(
    () => durationsSeconds[0] ?? defaultVariantDurationSeconds(variants)
  );
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const visibleVariants = filterVariantsForDuration(variants, durationSeconds);

  /** A duration switch keeps the visitor's picks by moving each line to the
   *  `groupKey` sibling sold at the new duration — the same remap the checkout
   *  steps apply to the basket. */
  function handleDurationChange(value: string) {
    const nextDuration = Number(value);
    setQuantities((current) =>
      toQuantities(
        remapTicketsForDuration(toTicketLines(current), variants, nextDuration)
      )
    );
    setDurationSeconds(nextDuration);
  }

  const ticketLines = toTicketLines(quantities);
  const hasPickableVariants = visibleVariants.length > 0;
  const hasSelection = visibleVariants.some((v) => (quantities[v.id] ?? 0) > 0);
  // Whether ANY offered duration sells something — the difference between "pick
  // another duration" and "this experience has nothing to sell right now",
  // which the widget has to say out loud rather than leave a permanently
  // disabled Book now unexplained. Mirrors the open checkout flow's tickets
  // step, which the flow bounces an empty basket back to.
  const hasSellableVariantsAnywhere = computeHasSellableVariants(
    variants,
    durationsSeconds
  );

  function handleBookNow() {
    // Not a `Checkout *` event, so the checkout layout's enrichment plugin
    // never stamps it — `experience_id` has to ride along explicitly.
    ampli.experienceBookingPanelBookNowClicked({
      experience_id: experienceId,
      availability_mode: 'open',
      widget_mode: 'open',
      ticket_count: ticketLines.reduce((sum, line) => sum + line.quantity, 0),
      duration_seconds: durationSeconds ?? undefined,
    });
    fetcher.submit(
      {
        intent: OPEN_TIME_SLOT_INTENT,
        // The location is deliberately not sent — `/open/location` owns it.
        duration: durationSeconds != null ? String(durationSeconds) : '',
        tickets: JSON.stringify(ticketLines),
      },
      { method: 'post' }
    );
  }

  const formattedDefaultPrice = defaultPrice ? (
    <FormattedPrice
      price={{
        vatInclusivePriceCents: defaultPrice.amountCents,
        vatExclusivePriceCents: defaultPrice.vatExclusiveAmountCents,
        currency: defaultPrice.currency,
        exponent: defaultPrice.exponent,
        vatExempt: defaultPrice.vatExempt,
      }}
    />
  ) : null;

  // The message for the no-picker states below, mirroring the open checkout
  // flow's tickets step (same wording, so the two share a translation): a
  // visitor who can't pick anything is told why rather than left with a
  // silently disabled Book now.
  const noVariantsMessage = hasSellableVariantsAnywhere ? (
    <FormattedMessage
      defaultMessage="No tickets are sold for this duration. Pick another duration to continue."
      description="Open-time-slot message shown when the selected duration has no ticket variants but another offered duration does"
    />
  ) : (
    <FormattedMessage
      defaultMessage="There are no tickets available to book for this experience right now."
      description="Open-time-slot message shown when the experience sells no ticket variants at any of its durations"
    />
  );

  return (
    <BookingPanelCard canProceedToCheckout={canProceedToCheckout}>
      <Stack gap="xlarge">
        <Stack gap="xsmall">
          {formattedDefaultPrice && (
            // Per-person is deliberately not spelled out here (unlike the
            // fixed-events panel): an open time slot's variants are as often a
            // rental as a seat, and the priced ticket list sits right below.
            <Text
              as="p"
              style="subheadline-small"
              color="primary"
              className={css({ padding: 'xsmall' })}
            >
              {showFromPrice ? (
                <FormattedMessage
                  defaultMessage="From {price}"
                  description="Cheapest-ticket price label above the open-time-slot widget's duration and ticket pickers, with 'From' prefix"
                  values={{ price: formattedDefaultPrice }}
                />
              ) : (
                formattedDefaultPrice
              )}
            </Text>
          )}

          <Stack gap="medium">
            {durationsSeconds.length > 0 && (
              <ChoiceGroup
                aria-label={intl.formatMessage({
                  defaultMessage: 'Select a duration',
                  description:
                    'Accessible label for the open-time-slot duration picker',
                })}
                value={
                  durationSeconds != null ? String(durationSeconds) : undefined
                }
                onChange={handleDurationChange}
              >
                {durationsSeconds.map((seconds) => (
                  <Choice
                    key={seconds}
                    value={String(seconds)}
                    className={durationChoiceStyles}
                  >
                    {formatDuration(intl, Math.round(seconds / 60))}
                  </Choice>
                ))}
              </ChoiceGroup>
            )}

            {hasPickableVariants ? (
              <TicketsSelector
                variants={visibleVariants}
                quantities={quantities}
                seatStep={seatStep}
                onQuantitiesChange={setQuantities}
              />
            ) : (
              <Text
                as="p"
                style="body-small-regular"
                className={css({
                  color: 'content.secondary',
                  textAlign: 'center',
                  paddingBlock: 'medium',
                })}
              >
                {noVariantsMessage}
              </Text>
            )}
          </Stack>
        </Stack>

        <Button
          appearance="primary"
          size="large"
          className={css({ width: 'full' })}
          // Book now always requires a selection: the checkout flow's own tickets
          // step now bounces an empty basket straight back to the visitor, so
          // letting Book now through with nothing picked would be a loop, not a
          // shortcut (same rule as that step's Continue).
          disabled={!hasSelection}
          busy={fetcher.state !== 'idle'}
          onClick={handleBookNow}
        >
          <FormattedMessage
            defaultMessage="Book now"
            description="Open-time-slot widget primary call-to-action that starts the checkout flow"
          />
        </Button>
      </Stack>
    </BookingPanelCard>
  );
}

/** The picker's id-keyed map as the basket-style ticket list the rules and the
 *  action speak. Zero lines are dropped — they carry no selection. */
function toTicketLines(
  quantities: Record<string, number>
): { variantId: string; quantity: number }[] {
  return Object.entries(quantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([variantId, quantity]) => ({ variantId, quantity }));
}

function toQuantities(
  tickets: readonly { variantId: string; quantity: number }[]
): Record<string, number> {
  const quantities: Record<string, number> = {};
  for (const { variantId, quantity } of tickets) {
    quantities[variantId] = quantity;
  }
  return quantities;
}
